import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { eachDayOfInterval, format } from "date-fns";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import { PlanSpanBar } from "./planSpanBar";
import {
  eventColorForId,
  groupResourcesByEmployee,
  parsePlanDayKey,
  projectColorForId,
  userInitials,
  weekNumbersForDays,
} from "./projectPlanUtils";

const LABEL_W = 260;
const MIN_DAY_W = 56;
const MAX_DAY_W = 180;
const DEFAULT_DAY_W = 92;
/** Always-visible empty lanes so drafts can be dropped even when the grid is full. */
const EMPTY_DROP_ROW_COUNT = 4;

const parseDayKey = parsePlanDayKey;

const eventHours = (event) => {
  const fromResources = (event.resources || []).reduce((sum, r) => {
    if (!r.start_time || !r.end_time) return sum;
    const s = new Date(r.start_time);
    const e = new Date(r.end_time);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return sum;
    return sum + Math.max(0, (e - s) / 3600000);
  }, 0);
  if (fromResources > 0) return Math.round(fromResources);
  return event.planned_hours || 0;
};

const uniqueUsers = (event) => {
  const map = new Map();
  (event.resources || []).forEach((r) => {
    if (r.user?.id) map.set(r.user.id, r.user);
    else if (r.user_id) map.set(r.user_id, r.user || { id: r.user_id, displayTitle: `#${r.user_id}` });
  });
  return Array.from(map.values());
};

export function buildScheduleRows(events, days, t) {
  const dayKeys = days.map((d) => format(d, "yyyy-MM-dd"));
  const lastIdx = dayKeys.length - 1;
  const dayIndex = Object.fromEntries(dayKeys.map((k, i) => [k, i]));

  const projects = new Map();
  (events || []).forEach((event) => {
    const startKey = parseDayKey(event.start_date);
    const endKey = parseDayKey(event.end_date) || startKey;
    if (!startKey || lastIdx < 0) return;

    // Skip events fully outside the visible window
    if (endKey < dayKeys[0] || startKey > dayKeys[lastIdx]) return;

    let startIdx = dayIndex[startKey];
    let endIdx = dayIndex[endKey];
    // Clip to visible window
    if (startIdx === undefined) startIdx = 0;
    if (endIdx === undefined) endIdx = lastIdx;
    if (startIdx > endIdx) [startIdx, endIdx] = [endIdx, startIdx];

    const pid = String(event.project_id || "none");
    const projectName =
      event.project?.displayTitle || event.project?.name || t("No project");

    if (!projects.has(pid)) {
      projects.set(pid, {
        id: pid,
        name: projectName,
        color: projectColorForId(pid),
        tasks: [],
      });
    }

    projects.get(pid).tasks.push({
      event,
      startIdx,
      endIdx,
      hours: eventHours(event),
      users: uniqueUsers(event),
      color: eventColorForId(event.id),
    });
  });

  return {
    dayKeys,
    projects: Array.from(projects.values())
      .filter((p) => p.tasks.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name, "nb")),
  };
}

export function ProjectPlanScheduleView({
  dateFrom,
  dateTo,
  periodType = "fortnight",
  events = [],
  capacityByDate = {},
  absencesByDate = null,
  holidaysByDate = null,
  loading = false,
  canEdit = true,
  dragKind = null,
  onEventClick,
  onNewEvent,
  onDayAction,
  onResourceDayClick,
  onEventDatesChange,
  onDraftDrop,
  onAssignResource,
}) {
  const { t } = useTranslation();
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [dropDay, setDropDay] = useState(null);
  const [assignTaskId, setAssignTaskId] = useState(null);
  const wrapperRef = useRef(null);
  /** Ignore the synthetic click browsers fire after a successful drop. */
  const suppressAddDayClickUntilRef = useRef(0);
  const [availableWidth, setAvailableWidth] = useState(0);
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const absenceMap = useMemo(
    () =>
      absencesByDate instanceof Map
        ? absencesByDate
        : new Map(Object.entries(absencesByDate || {})),
    [absencesByDate]
  );
  const holidayMap = useMemo(
    () =>
      holidaysByDate instanceof Map
        ? holidaysByDate
        : new Map(Object.entries(holidaysByDate || {})),
    [holidaysByDate]
  );

  useLayoutEffect(() => {
    if (!wrapperRef.current || typeof ResizeObserver === "undefined") return undefined;
    const parent = wrapperRef.current.parentElement;
    if (!parent) return undefined;
    const ro = new ResizeObserver(() => {
      setAvailableWidth(parent.clientWidth);
    });
    ro.observe(parent);
    setAvailableWidth(parent.clientWidth);
    return () => ro.disconnect();
  }, []);

  const days = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    return eachDayOfInterval({
      start: new Date(`${dateFrom}T12:00:00`),
      end: new Date(`${dateTo}T12:00:00`),
    });
  }, [dateFrom, dateTo]);

  const { dayKeys, projects } = useMemo(
    () => buildScheduleRows(events, days, t),
    [events, days, t]
  );

  const weekNumbersByDay = useMemo(
    () => weekNumbersForDays(days),
    [days]
  );

  const showWeekNumbers =
    periodType === "week" || periodType === "fortnight" || periodType === "month";

  const hasAbsenceRow = useMemo(
    () => dayKeys.some((dKey) => (absenceMap.get(dKey) || []).length > 0),
    [dayKeys, absenceMap]
  );

  const hasHolidayRow = useMemo(
    () => dayKeys.some((dKey) => holidayMap.get(dKey)),
    [dayKeys, holidayMap]
  );

  const dayCount = days.length;
  const DAY_W = useMemo(() => {
    if (!dayCount || availableWidth <= 0) return DEFAULT_DAY_W;
    const natural = (availableWidth - LABEL_W) / dayCount;
    return Math.max(MIN_DAY_W, Math.min(MAX_DAY_W, Math.round(natural)));
  }, [availableWidth, dayCount]);

  const handleSpanChange = useCallback(
    async (task, newStartIdx, newEndIdx) => {
      const resources = task.event.resources || [];
      if (resources.length > 0) return;

      const startDate = dayKeys[newStartIdx];
      const endDate = dayKeys[newEndIdx];
      if (!startDate || !endDate) return;

      await onEventDatesChange?.(task.event, {
        start_date: startDate,
        end_date: endDate,
        resources: [],
      });
    },
    [dayKeys, onEventDatesChange]
  );

  const handleEmployeeDayMove = useCallback(
    async (event, employee, fromDayKey, newStartIdx, newEndIdx) => {
      const newDayKey = dayKeys[newStartIdx];
      if (!newDayKey || newStartIdx !== newEndIdx || newDayKey === fromDayKey) return;

      const updatedResources = (event.resources || []).map((resource) => {
        const resourceDay = parseDayKey(resource.start_time);
        if (
          String(resource.user_id) !== String(employee.userId) ||
          resourceDay !== fromDayKey
        ) {
          return resource;
        }

        const startDate = new Date(resource.start_time);
        const endDate = new Date(resource.end_time);
        const startH = Number.isNaN(startDate.getTime()) ? 8 : startDate.getHours();
        const startM = Number.isNaN(startDate.getTime()) ? 0 : startDate.getMinutes();
        const endH = Number.isNaN(endDate.getTime()) ? 16 : endDate.getHours();
        const endM = Number.isNaN(endDate.getTime()) ? 0 : endDate.getMinutes();

        const nextStart = new Date(
          `${newDayKey}T${String(startH).padStart(2, "0")}:${String(startM).padStart(2, "0")}:00`
        );
        const nextEnd = new Date(
          `${newDayKey}T${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}:00`
        );

        return {
          ...resource,
          start_time: nextStart.toISOString(),
          end_time: nextEnd.toISOString(),
        };
      });

      await onEventDatesChange?.(event, { resources: updatedResources });
    },
    [dayKeys, onEventDatesChange]
  );

  if (loading) {
    return (
      <div ref={wrapperRef} className="flex w-full flex-col items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2B6CFF]" />
        <p className="mt-4 text-sm text-[#6B7682]">{t("project.plan.loading")}</p>
      </div>
    );
  }

  if (!days.length) return <div ref={wrapperRef} />;

  const gridWidth = dayCount * DAY_W;
  const todayIdx = dayKeys.indexOf(todayKey);
  const todayOffset = todayIdx >= 0 ? LABEL_W + todayIdx * DAY_W + DAY_W / 2 : -1;

  const acceptDraftDragOver = (e, dKey) => {
    if (!canEdit || !onDraftDrop) return false;
    // Prefer dragKind when available; also allow MIME type for reliability.
    const isDraft =
      dragKind === "draft" ||
      (e.dataTransfer?.types || []).includes("application/x-plan-draft-id");
    if (!isDraft) return false;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropDay(dKey);
    return true;
  };

  const handleDraftDropOnDay = (e, dKey) => {
    e.preventDefault();
    setDropDay(null);
    const draftId = e.dataTransfer.getData("application/x-plan-draft-id");
    if (draftId && onDraftDrop) {
      onDraftDrop(Number(draftId), dKey);
      return true;
    }
    return false;
  };

  const handleDayCellClick = (dKey) => {
    if (onDayAction) {
      onDayAction(dKey);
      return;
    }
    onNewEvent?.(dKey);
  };

  const renderDayCells = (rowClass) =>
    dayKeys.map((dKey, i) => {
      const isWeekend = days[i].getDay() === 0 || days[i].getDay() === 6;
      const isHoliday = Boolean(holidayMap.get(dKey));
      const isToday = dKey === format(new Date(), "yyyy-MM-dd");
      const isDrop = dropDay === dKey && dragKind === "draft";

      return (
        <div
          key={dKey}
          className={`cell ${rowClass} ${isWeekend ? "weekend" : ""} ${isHoliday ? "holiday" : ""} ${isToday ? "today" : ""} ${isDrop ? "dropping" : ""}`}
          onDragOver={(e) => {
            acceptDraftDragOver(e, dKey);
          }}
          onDragLeave={() => {
            if (dropDay === dKey) setDropDay(null);
          }}
          onDrop={(e) => {
            handleDraftDropOnDay(e, dKey);
          }}
          onClick={() => handleDayCellClick(dKey)}
        />
      );
    });

  return (
    <div
      ref={wrapperRef}
      className={`cal-grid relative ${projects.length === 0 ? "is-empty min-h-full" : ""} ${dragKind === "draft" ? "is-draft-dropping" : ""}`}
      style={{ gridTemplateColumns: `${LABEL_W}px ${gridWidth}px` }}
      onDragLeave={() => setDropDay(null)}
    >
      {todayOffset > 0 ? (
        <div className="pp-today-line" style={{ left: todayOffset }} />
      ) : null}
      <div className="cal-head-left">{t("project.plan.toolbar.project_task")}</div>
      <div
        className="cal-head-days"
        style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
      >
        {days.map((day) => {
          const dKey = format(day, "yyyy-MM-dd");
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const isHoliday = Boolean(holidayMap.get(dKey));
          const isToday = dKey === format(new Date(), "yyyy-MM-dd");
          const dow = format(day, "EEE", { locale: currentDateFnsLocale() }).toUpperCase();
          const cap = capacityByDate[dKey];
          const holiday = holidayMap.get(dKey);
          const weekNum = weekNumbersByDay.get(dKey);

          return (
            <div
              key={dKey}
              className={`day-head ${isWeekend ? "weekend" : ""} ${isHoliday ? "holiday" : ""} ${isToday ? "today" : ""}`}
            >
              {showWeekNumbers && weekNum ? (
                <span className="week-num">
                  {t("project.plan.schedule.week_short", { week: weekNum })}
                </span>
              ) : null}
              <span className="dow">{dow}</span>
              <span className="dom">{format(day, "d")}</span>
              {canEdit && onDayAction ? (
                <button
                  type="button"
                  className="pp-day-add"
                  aria-label={t("project.plan.day_action.add_aria", { date: dKey })}
                  title={t("project.plan.day_action.add_aria", { date: dKey })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDayAction(dKey);
                  }}
                >
                  +
                </button>
              ) : null}
              {holiday ? (
                <span className="pp-holiday-label" title={holiday.name}>
                  {holiday.name}
                </span>
              ) : null}
              {cap ? (
                <div className="cap-bar">
                  <span
                    className={`used ${cap.freeResources <= 0 ? "warn" : ""}`}
                    style={{ flex: cap.assignedResources || 1 }}
                  />
                  <span className="free" style={{ flex: Math.max(cap.freeResources, 0) }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {hasHolidayRow ? (
        <>
          <div className="cal-head-left pp-holiday-row-label">
            {t("project.plan.holidays.title")}
          </div>
          <div
            className="cal-holiday-days"
            style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
          >
            {dayKeys.map((dKey, i) => {
              const holiday = holidayMap.get(dKey);
              const isWeekend = days[i].getDay() === 0 || days[i].getDay() === 6;
              const isToday = dKey === todayKey;
              return (
                <div
                  key={dKey}
                  className={`day-holiday ${isWeekend ? "weekend" : ""} ${holiday ? "is-holiday" : ""} ${isToday ? "today" : ""}`}
                >
                  {holiday ? (
                    <span className="pp-holiday-chip" title={holiday.name}>
                      {holiday.name}
                    </span>
                  ) : (
                    <span className="day-holiday-empty">—</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {hasAbsenceRow ? (
        <>
          <div className="cal-head-left pp-absence-row-label">
            {t("project.plan.absences.title")}
          </div>
          <div
            className="cal-absence-days"
            style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
          >
            {dayKeys.map((dKey, i) => {
              const absent = absenceMap.get(dKey) || [];
              const isWeekend = days[i].getDay() === 0 || days[i].getDay() === 6;
              const isToday = dKey === todayKey;
              return (
                <div
                  key={dKey}
                  className={`day-absence ${isWeekend ? "weekend" : ""} ${isToday ? "today" : ""}`}
                >
                  {absent.length === 0 ? (
                    <span className="day-absence-empty">—</span>
                  ) : (
                    absent.map((entry) => (
                      <span
                        key={entry.userId}
                        className="pp-absence-chip"
                        title={
                          entry.absenceLabel
                            ? `${entry.displayTitle} — ${entry.absenceLabel}`
                            : entry.displayTitle
                        }
                      >
                        <span className="pp-absence-chip-name">{entry.displayTitle}</span>
                        {entry.absenceLabel ? (
                          <span className="pp-absence-chip-type">{entry.absenceLabel}</span>
                        ) : null}
                      </span>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {projects.length === 0 ? (
        <div className="contents">
          <div className="group-label items-center">
            <span className="text-sm italic text-[#9AA3AF]">
              {t("project.plan.no_events")}
            </span>
          </div>
          <div
            className="group-row-cells"
            style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
          >
            {dayKeys.map((dKey, i) => (
              <div
                key={dKey}
                className={`gcell ${days[i].getDay() === 0 || days[i].getDay() === 6 ? "weekend" : ""}`}
              />
            ))}
          </div>
        </div>
      ) : (
        projects.map((project) => (
          <div key={project.id} className="contents">
            <div className="group-label">
              <span className="group-bar shrink-0 self-stretch" style={{ backgroundColor: project.color }} />
              <span className="group-name truncate">{project.name}</span>
              <span className="group-count">{project.tasks.length}</span>
            </div>
            <div
              className="group-row-cells"
              style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
            >
              {dayKeys.map((dKey, i) => {
                const isDrop = dropDay === dKey && dragKind === "draft";
                return (
                  <div
                    key={dKey}
                    className={`gcell ${days[i].getDay() === 0 || days[i].getDay() === 6 ? "weekend" : ""} ${isDrop ? "dropping" : ""}`}
                    onDragOver={(e) => {
                      acceptDraftDragOver(e, dKey);
                    }}
                    onDragLeave={() => {
                      if (dropDay === dKey) setDropDay(null);
                    }}
                    onDrop={(e) => {
                      handleDraftDropOnDay(e, dKey);
                    }}
                  />
                );
              })}
            </div>

            {project.tasks.map((task) => {
              const resources = task.event.resources || [];
              const employees = groupResourcesByEmployee(resources);
              const showEmployeeRows = employees.length > 0;

              return (
                <div key={task.event.id} className="contents">
                  <div
                    className={`task-row-label ${showEmployeeRows ? "task-row-label--with-crew" : ""}`}
                    onClick={() => {
                      setSelectedEventId(task.event.id);
                      onEventClick?.(task.event);
                    }}
                  >
                    <span className="tname truncate">{task.event.title}</span>
                    <span className="thours shrink-0">{task.hours > 0 ? `${task.hours}h` : ""}</span>
                  </div>
                  <div
                    className={`task-row-cells ${showEmployeeRows ? "task-row-cells--summary" : ""} ${assignTaskId === task.event.id && !showEmployeeRows ? "assigning" : ""}`}
                    style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
                    onDragOver={
                      !showEmployeeRows && dragKind === "resource"
                        ? (e) => {
                            e.preventDefault();
                            setAssignTaskId(task.event.id);
                          }
                        : undefined
                    }
                    onDragLeave={!showEmployeeRows ? () => setAssignTaskId(null) : undefined}
                  >
                    {/* Always keep draft-drop targets — crew summary rows used
                        to replace interactive cells and silently blocked drops. */}
                    {renderDayCells("")}
                    {!showEmployeeRows ? (
                      <PlanSpanBar
                        startIdx={task.startIdx}
                        endIdx={task.endIdx}
                        dayWidth={DAY_W}
                        color={task.color}
                        title={task.event.title}
                        users={task.users}
                        selected={selectedEventId === task.event.id}
                        readOnly={!canEdit}
                        onChange={(s, e) => handleSpanChange(task, s, e)}
                      />
                    ) : (
                      <PlanSpanBar
                        startIdx={task.startIdx}
                        endIdx={task.endIdx}
                        dayWidth={DAY_W}
                        color={task.color}
                        title={task.event.title}
                        users={task.users}
                        selected={selectedEventId === task.event.id}
                        readOnly
                        summary
                      />
                    )}
                  </div>

                  {showEmployeeRows
                    ? employees.map((employee) => (
                          <div key={`${task.event.id}-${employee.key}`} className="contents">
                            <div className="assignee-label">
                              <span
                                className="av inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold text-white"
                                style={{ backgroundColor: task.color }}
                              >
                                {userInitials(employee.label)}
                              </span>
                              <span className="assignee-name min-w-0 truncate">
                                {employee.label}
                                {employee.ended || employee.employmentEnds ? (
                                  <span className="ml-1 text-[10px] font-medium text-rose-700">
                                    {t(
                                      employee.ended
                                        ? "employment.end_date.ended_badge"
                                        : "employment.end_date.until",
                                      {
                                        date: String(employee.employmentEnds || "").substring(0, 10),
                                      }
                                    )}
                                  </span>
                                ) : null}
                              </span>
                              {employee.dayCount > 1 ? (
                                <span className="assignee-day-badge">· {employee.dayCount}d</span>
                              ) : null}
                            </div>
                            <div
                              className={`assignee-row ${assignTaskId === task.event.id ? "assigning" : ""}`}
                              style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
                              onDragOver={(e) => {
                                if (dragKind === "resource") {
                                  e.preventDefault();
                                  setAssignTaskId(task.event.id);
                                }
                              }}
                              onDragLeave={() => setAssignTaskId(null)}
                            >
                              {dayKeys.map((dKey, i) => {
                                const isWeekend =
                                  days[i].getDay() === 0 || days[i].getDay() === 6;
                                const isToday = dKey === todayKey;
                                const isDrop =
                                  dropDay === dKey &&
                                  (dragKind === "draft" ||
                                    assignTaskId === task.event.id);
                                const alreadyAssigned =
                                  employee.assignedDayKeys?.includes(dKey);
                                const canAddDay =
                                  canEdit &&
                                  onAssignResource &&
                                  !alreadyAssigned;
                                return (
                                  <div
                                    key={dKey}
                                    className={`cell ${isWeekend ? "weekend" : ""} ${isToday ? "today" : ""} ${isDrop ? "dropping" : ""} ${canAddDay ? "cell--add-day" : ""}`}
                                    title={
                                      canAddDay
                                        ? t("project.plan.resource_row.add_day_hint")
                                        : undefined
                                    }
                                    onClick={
                                      canAddDay
                                        ? (e) => {
                                            // Ignore if a drag just finished / bar handled it.
                                            if (e.defaultPrevented) return;
                                            // Drop handlers call preventDefault, but the
                                            // follow-up click still fires — suppress it.
                                            if (
                                              Date.now() <
                                              suppressAddDayClickUntilRef.current
                                            ) {
                                              return;
                                            }
                                            onAssignResource(
                                              task.event,
                                              employee.userId,
                                              dKey
                                            );
                                          }
                                        : undefined
                                    }
                                    onDragOver={(e) => {
                                      if (!canEdit) return;
                                      if (acceptDraftDragOver(e, dKey)) return;
                                      if (dragKind !== "resource") return;
                                      e.preventDefault();
                                      setDropDay(dKey);
                                      setAssignTaskId(task.event.id);
                                    }}
                                    onDragLeave={() => {
                                      if (dropDay === dKey) setDropDay(null);
                                    }}
                                    onDrop={(e) => {
                                      if (handleDraftDropOnDay(e, dKey)) {
                                        suppressAddDayClickUntilRef.current =
                                          Date.now() + 400;
                                        setAssignTaskId(null);
                                        return;
                                      }
                                      e.preventDefault();
                                      const resourceId = e.dataTransfer.getData(
                                        "application/x-plan-resource-id"
                                      );
                                      if (resourceId && onAssignResource) {
                                        suppressAddDayClickUntilRef.current =
                                          Date.now() + 400;
                                        onAssignResource(
                                          task.event,
                                          Number(resourceId),
                                          dKey
                                        );
                                      }
                                      setDropDay(null);
                                      setAssignTaskId(null);
                                    }}
                                  />
                                );
                              })}
                              {employee.daySegments.map(({ dayKey, resource }) => {
                                const dayIdx = dayKeys.indexOf(dayKey);
                                if (dayIdx < 0) return null;
                                return (
                                  <PlanSpanBar
                                    key={dayKey}
                                    startIdx={dayIdx}
                                    endIdx={dayIdx}
                                    dayWidth={DAY_W}
                                    color={task.color}
                                    title={employee.label}
                                    compact
                                    disableResize
                                    readOnly={!canEdit}
                                    onSelect={
                                      onResourceDayClick
                                        ? () =>
                                            onResourceDayClick({
                                              event: task.event,
                                              resource,
                                              dayKey,
                                              employee,
                                            })
                                        : undefined
                                    }
                                    onChange={
                                      canEdit
                                        ? (s, e) =>
                                            handleEmployeeDayMove(
                                              task.event,
                                              employee,
                                              dayKey,
                                              s,
                                              e
                                            )
                                        : undefined
                                    }
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))
                    : null}
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* Always-visible empty lanes — easy drop targets for drafts */}
      {Array.from({ length: EMPTY_DROP_ROW_COUNT }, (_, idx) => (
        <div key={`drop-lane-${idx}`} className="contents">
          <div
            className={`task-row-label drop-lane-label ${dragKind === "draft" ? "drop-lane-label--active" : ""}`}
          >
            {idx === 0 ? (
              <span className="drop-lane-hint truncate">
                {t("project.plan.drop_lane_hint")}
              </span>
            ) : null}
          </div>
          <div
            className={`task-row-cells drop-lane-cells ${dragKind === "draft" ? "drop-lane-cells--active" : ""}`}
            style={{ gridTemplateColumns: `repeat(${dayCount}, ${DAY_W}px)` }}
          >
            {renderDayCells("drop-lane")}
          </div>
        </div>
      ))}
    </div>
  );
}
